import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Badge, Button, Col, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner, Table } from "reactstrap";
import ExpenseManagerService from "../../../Services/expenseManager/ExpenseManagerService";
import { formatMoney } from "../../../utils/formatMoney";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import { can, formatShortDate, FREQUENCY_LABELS, PERMISSIONS } from "./expenseManagerUtils";

const blank = {
  company_id: "", title: "", description: "", category_id: "", supplier_name: "",
  net_amount: "", tax_amount: "", gross_amount: "", currency_code: "",
  frequency: "monthly", interval_value: 1, day_of_month: 1, start_date: "", next_due_date: "",
  end_date: "", auto_create_draft: true, is_active: true,
};

export default function RecurringTab({ meta, onChanged }) {
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  const query = useQuery({
    queryKey: ["expenseManagerRecurring"],
    queryFn: ExpenseManagerService.getRecurring,
  });
  const categoriesQuery = useQuery({
    queryKey: ["expenseManagerCategories"],
    queryFn: ExpenseManagerService.getCategories,
    staleTime: 60_000,
  });

  const canManage = can(meta, PERMISSIONS.MANAGE_CATEGORIES);

  if (query.isLoading) return <div className="text-center py-5"><Spinner /></div>;
  if (query.isError) return <div className="alert alert-danger">{getApiErrorMessage(query.error, "Could not load recurring templates.")}</div>;

  const rows = query.data?.data || [];

  const categoryOptions = (() => {
    const flat = [];
    const walk = (items, depth) => {
      (items || []).forEach((item) => {
        flat.push({ id: item.id, name: `${"— ".repeat(depth)}${item.name}` });
        walk(item.children, depth + 1);
      });
    };
    walk(categoriesQuery.data?.data, 0);
    return flat;
  })();

  const refresh = () => {
    query.refetch();
    if (onChanged) onChanged();
  };

  const save = async () => {
    setSaving(true);
    const payload = {
      company_id: Number(editor.company_id),
      title: editor.title.trim(),
      description: editor.description || null,
      category_id: editor.category_id ? Number(editor.category_id) : null,
      supplier_name: editor.supplier_name || null,
      net_amount: String(Number(editor.net_amount)),
      tax_amount: editor.tax_amount === "" ? null : String(Number(editor.tax_amount)),
      gross_amount: String(Number(editor.gross_amount)),
      currency_code: editor.currency_code.toUpperCase(),
      frequency: editor.frequency,
      interval_value: Number(editor.interval_value),
      day_of_month: editor.day_of_month ? Number(editor.day_of_month) : null,
      start_date: editor.start_date,
      next_due_date: editor.next_due_date,
      end_date: editor.end_date || null,
      auto_create_draft: Boolean(editor.auto_create_draft),
      is_active: Boolean(editor.is_active),
    };
    try {
      if (editor.id) await ExpenseManagerService.updateRecurring(editor.id, payload);
      else await ExpenseManagerService.createRecurring(payload);
      toast.success(editor.id ? "Recurring template updated." : "Recurring template created.");
      setEditor(null);
      refresh();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Could not save the recurring template."));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row) => {
    try {
      await ExpenseManagerService.updateRecurring(row.id, { is_active: !row.is_active });
      refresh();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Could not update the template."));
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const result = await ExpenseManagerService.generateRecurring();
      toast.success(`Generated ${result?.data?.generated ?? 0} draft expense(s).`);
      refresh();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Could not generate drafts."));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="em-recurring">
      <Row className="mb-2">
        <Col className="d-flex align-items-center gap-2">
          <span className="text-muted small">{rows.length} templates</span>
          {canManage && (
            <>
              <Button size="sm" color="primary" onClick={() => setEditor({ ...blank, company_id: meta.companies[0]?.id || "", currency_code: meta.companies[0]?.base_currency || "TND", start_date: new Date().toISOString().slice(0, 10), next_due_date: new Date().toISOString().slice(0, 10) })}>
                New template
              </Button>
              <Button size="sm" color="light" onClick={generate} disabled={generating}>{generating ? "Generating…" : "Generate drafts now"}</Button>
            </>
          )}
        </Col>
      </Row>

      <Table size="sm" className="em-table align-middle">
        <thead>
          <tr>
            <th>Title</th>
            <th>Company</th>
            <th>Amount</th>
            <th>Frequency</th>
            <th>Next due</th>
            <th>Status</th>
            {canManage && <th className="text-end">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={canManage ? 7 : 6} className="text-center text-muted py-4">No recurring templates.</td></tr>}
          {rows.map((row) => (
            <tr key={row.id}>
              <td>
                {row.title}
                <div className="text-muted small">{row.supplier_name}</div>
              </td>
              <td>{meta.companies.find((c) => c.id === row.company_id)?.name || `#${row.company_id}`}</td>
              <td className="text-nowrap">{formatMoney(row.gross_amount, row.currency_code)}</td>
              <td>{FREQUENCY_LABELS[row.frequency] || row.frequency}{row.interval_value > 1 ? ` ×${row.interval_value}` : ""}</td>
              <td className="text-nowrap">
                {formatShortDate(row.next_due_date)}
                {row.is_overdue ? <Badge color="danger" pill className="ms-1">overdue</Badge> : null}
                {row.days_until_due != null && !row.is_overdue && row.days_until_due >= 0 && row.days_until_due <= 14 ? (
                  <Badge color={row.days_until_due <= 7 ? "warning" : "info"} pill className="ms-1">in {row.days_until_due}d</Badge>
                ) : null}
              </td>
              <td><Badge color={row.is_active ? "success" : "secondary"} pill>{row.is_active ? "Active" : "Paused"}</Badge></td>
              {canManage && (
                <td className="text-end text-nowrap">
                  <Button size="sm" color="link" onClick={() => setEditor({
                    ...row, company_id: row.company_id, category_id: row.category_id || "", end_date: row.end_date || "",
                  })}>Edit</Button>
                  <Button size="sm" color="link" onClick={() => toggleActive(row)}>{row.is_active ? "Pause" : "Activate"}</Button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>

      {editor && (
        <Modal isOpen toggle={() => setEditor(null)} size="lg" backdrop="static">
          <Form onSubmit={(e) => { e.preventDefault(); save(); }}>
            <ModalHeader toggle={() => setEditor(null)}>{editor.id ? "Edit recurring template" : "New recurring template"}</ModalHeader>
            <ModalBody>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Company *</Label>
                    <Input type="select" value={editor.company_id} onChange={(e) => { const base = meta.companies.find((c) => c.id === Number(e.target.value))?.base_currency; setEditor((s) => ({ ...s, company_id: e.target.value, currency_code: base || s.currency_code })); }}>
                      <option value="">Select company…</option>
                      {meta.companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Title *</Label>
                    <Input value={editor.title} onChange={(e) => setEditor((s) => ({ ...s, title: e.target.value }))} required />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Category</Label>
                    <Input type="select" value={editor.category_id} onChange={(e) => setEditor((s) => ({ ...s, category_id: e.target.value }))}>
                      <option value="">—</option>
                      {categoryOptions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Supplier</Label>
                    <Input value={editor.supplier_name || ""} onChange={(e) => setEditor((s) => ({ ...s, supplier_name: e.target.value }))} />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>Net *</Label>
                    <Input type="number" step="0.001" min="0" value={editor.net_amount} onChange={(e) => setEditor((s) => ({ ...s, net_amount: e.target.value }))} required />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>Tax</Label>
                    <Input type="number" step="0.001" min="0" value={editor.tax_amount} onChange={(e) => setEditor((s) => ({ ...s, tax_amount: e.target.value }))} />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>Gross *</Label>
                    <Input type="number" step="0.001" min="0" value={editor.gross_amount} onChange={(e) => setEditor((s) => ({ ...s, gross_amount: e.target.value }))} required />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>Currency *</Label>
                    <Input type="select" value={editor.currency_code} onChange={(e) => setEditor((s) => ({ ...s, currency_code: e.target.value }))}>
                      {meta.currencies.map((c) => <option key={c} value={c}>{c}</option>)}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>Frequency *</Label>
                    <Input type="select" value={editor.frequency} onChange={(e) => setEditor((s) => ({ ...s, frequency: e.target.value }))}>
                      {meta.frequencies.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>Interval (months) *</Label>
                    <Input type="number" min="1" max="12" value={editor.interval_value} onChange={(e) => setEditor((s) => ({ ...s, interval_value: e.target.value }))} />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>Day of month</Label>
                    <Input type="number" min="1" max="31" value={editor.day_of_month || ""} onChange={(e) => setEditor((s) => ({ ...s, day_of_month: e.target.value }))} />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>Start date *</Label>
                    <Input type="date" value={editor.start_date} onChange={(e) => setEditor((s) => ({ ...s, start_date: e.target.value }))} required />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>Next due date *</Label>
                    <Input type="date" value={editor.next_due_date} onChange={(e) => setEditor((s) => ({ ...s, next_due_date: e.target.value }))} required />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>End date</Label>
                    <Input type="date" value={editor.end_date || ""} onChange={(e) => setEditor((s) => ({ ...s, end_date: e.target.value }))} />
                  </FormGroup>
                </Col>
                <Col md={6} className="d-flex align-items-center gap-2">
                  <Input type="checkbox" checked={Boolean(editor.auto_create_draft)} onChange={(e) => setEditor((s) => ({ ...s, auto_create_draft: e.target.checked }))} style={{ width: "auto" }} />
                  <Label>Auto-create draft each interval</Label>
                </Col>
                <Col md={6} className="d-flex align-items-center gap-2">
                  <Input type="checkbox" checked={Boolean(editor.is_active)} onChange={(e) => setEditor((s) => ({ ...s, is_active: e.target.checked }))} style={{ width: "auto" }} />
                  <Label>Active</Label>
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button color="light" onClick={() => setEditor(null)} disabled={saving}>Cancel</Button>
              <Button color="primary" type="submit" disabled={saving || !editor.title.trim()}>{saving ? "Saving…" : "Save template"}</Button>
            </ModalFooter>
          </Form>
        </Modal>
      )}
    </div>
  );
}